/**
 * Client-side order-safety settings. In the old proxy build these were server env flags
 * (`GREENBOOK_ALLOW_REAL_ORDERS` / `GREENBOOK_ENABLE_LIMIT_ORDERS`). greenbook is now a
 * pure-static, direct-to-API app with no server, so the safety rail lives here in the
 * browser. Real money is armed by the global trading mode + the trade scope (see
 * lib/orderClient.ts — the order SDK is built with `realMoney.enabled` only then), and the
 * ticket offers `order_type:limit` only when this setting is on AND the venue's served
 * constraints list `limit`.
 *
 * Defaults to OFF — a fresh install can only place market orders until the user opts in.
 */
import { useSyncExternalStore } from "react";

export interface OrderSettings {
  /** Allow resting limit orders (venue must also support them). */
  enable_limit_orders: boolean;
}

// Real-vs-paper is now the GLOBAL trading mode (lib/venues.ts), not a per-setting toggle.
const KEY = "gb.order-settings";
const DEFAULTS: OrderSettings = { enable_limit_orders: false };

function read(): OrderSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<OrderSettings>;
    return { enable_limit_orders: parsed.enable_limit_orders === true };
  } catch {
    return DEFAULTS;
  }
}

let settings: OrderSettings = read();
const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}

/** Snapshot for non-React callers (the config router in api.ts). */
export function getOrderSettings(): OrderSettings {
  return settings;
}

export function setOrderSetting<K extends keyof OrderSettings>(key: K, value: OrderSettings[K]): void {
  settings = { ...settings, [key]: value };
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    /* ignore storage failures — value still lives in memory for this session */
  }
  emit();
}

export const settingsStore = {
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  getSnapshot(): OrderSettings {
    return settings;
  },
};

export function useOrderSettings(): OrderSettings {
  return useSyncExternalStore(settingsStore.subscribe, settingsStore.getSnapshot);
}
